import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    LinearProgress,
    Button,
    Chip,
    IconButton,
    CircularProgress,
} from '@mui/material';
import { Download as DownloadIcon, Refresh as RefreshIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import { fetchDownloads, downloadFile, deleteDownload } from 'slices/documentsActions';
import { closeModal } from 'slices/modalsSlice';
import { addSnackbar, SnackbarTypes } from 'slices/snackbarsSlice';
import BaseModal, { BaseModalRef } from '../BaseModal';
import { DownloadItem, DownloadStatus } from 'interfaces/downloads';
import { formatBytes } from 'utilities/helpers';

// must be unique
export const modalName = 'downloadsModal';

// serializable
export interface IDownloadsModalProps {}

// non serializable
export interface IDownloadsModalCallbacks {}

const REFRESH_INTERVAL = 5000;

const DownloadsModal = ({ id, zIndex, props, callbacks }: { id: string, zIndex: number, props: IDownloadsModalProps, callbacks: IDownloadsModalCallbacks }) => {
    const dispatch = useAppDispatch();
    const { downloads, loading, processing } = useAppSelector((state) => state.downloads);
    const modalRef = useRef<BaseModalRef>(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (props && !open) {
            setOpen(true);
        } else {
            setOpen(false);
        }
    }, [props]);

    useEffect(() => {
        if (open) {
            dispatch(fetchDownloads());
        }
    }, [open, dispatch]);

    useEffect(() => {
        if (!open) return;

        const interval = setInterval(() => {
            dispatch(fetchDownloads());
        }, REFRESH_INTERVAL);

        return () => clearInterval(interval);
    }, [open, dispatch]);

    const handleCloseModal = () => {
        dispatch(closeModal({ modal: modalName, id }));
    };

    const handleRefresh = () => {
        dispatch(fetchDownloads());
    };

    const handleDownload = (download: DownloadItem) => {
        window.open(download.download_url, '_blank');
    };

    const handleDelete = async (download: DownloadItem) => {
        try {
            await dispatch(deleteDownload(download.signature)).unwrap();
            handleRefresh();
            dispatch(addSnackbar({
                type: SnackbarTypes.SUCCESS,
                message: 'Download request deleted',
            }));
        } catch (error: any) {
            dispatch(addSnackbar({
                type: SnackbarTypes.ERROR,
                message: error?.data?.message || 'Failed to delete download request',
            }));
        }
    };

    const isDeleteLoading = (signature: string) => {
        return processing.includes(signature);
    };

    const getStatusColor = (status: DownloadStatus) => {
        switch (status) {
            case DownloadStatus.STARTING:
                return 'info';
            case DownloadStatus.READY:
                return 'success';
            case DownloadStatus.FAILED:
                return 'error';
            case DownloadStatus.PROCESSING:
            case DownloadStatus.UPLOADING:
                return 'warning';
            default:
                return 'default';
        }
    };

    const getStatusText = (status: DownloadStatus) => {
        switch (status) {
            case DownloadStatus.STARTING:
                return 'Starting';
            case DownloadStatus.READY:
                return 'Ready to Download';
            case DownloadStatus.FAILED:
                return 'Failed';
            case DownloadStatus.PROCESSING:
                return 'Processing';
            case DownloadStatus.UPLOADING:
                return 'Uploading';
            default:
                return 'Unknown';
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Unknown';
        return new Date(dateString).toLocaleString();
    };

    const renderDownloadItem = (download: DownloadItem) => {
        const canDownload = download.status === DownloadStatus.READY;

        return (
            <ListItem key={download.signature} divider>
                <ListItemText
                    primary={
                        <Box display="flex" alignItems="center" justifyContent="space-between" padding={1} gap={1}>
                            <Typography variant="subtitle2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                {download.name && (
                                    <>
                                        {download.name}
                                        <br/>
                                    </>
                                )}
                                <Typography component="span" variant="body2" color={download.name ? 'text.secondary' : undefined}>
                                    {download.name ? 'Requested' : 'Download:'} {formatDate(download.created_at)}
                                    {download.filesize ? ` (${formatBytes(download.filesize ?? 0)})` : ''}
                                </Typography>
                                <br/>
                                <Typography component="span" variant="body2" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                    {download.expires_at ? ` Expires ${formatDate(download.expires_at)}` : ''}
                                </Typography>
                            </Typography>
                            <Chip
                                label={getStatusText(download.status)}
                                color={getStatusColor(download.status)}
                                size="small"
                            />
                        </Box>
                    }
                />
                {
                    canDownload && (
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownload(download)}
                        size="small"
                    >
                        Download
                    </Button>
                )}
                {isDeleteLoading(download.signature) ? (
                    <CircularProgress size={24} sx={{ ml: 1 }} />
                ) : (
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(download)}
                        sx={{ ml: 1 }}
                    >
                        <DeleteIcon />
                    </IconButton>
                )}
            </ListItem>
        );
    };

    return (
        <BaseModal
            ref={modalRef}
            title="My Downloads"
            titleComponent={
                <Box display="flex" alignItems="center" gap={1} sx={{ p: 2, px: 3 }}>
                    <Typography variant="h6">My Downloads</Typography>
                    {loading && <CircularProgress size={16} />}
                </Box>
            }
            open={open}
            onClose={handleCloseModal}
            showCloseButton
            maxWidth="md"
            actions={
                <Box display="flex" gap={1}>
                    <Button onClick={handleCloseModal}>Close</Button>
                </Box>
            }
        >
            <Box>
                {
                    downloads.length === 0 ? (
                        <Box textAlign="center" p={3}>
                            <Typography variant="body1" color="text.secondary">
                                No downloads found
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Downloads you request will appear here
                            </Typography>
                        </Box>
                    ) : (
                        <List>
                            {downloads.map(renderDownloadItem)}
                        </List>
                    )
                }
            </Box>
        </BaseModal>
    );
};

export default DownloadsModal;
